import { api } from '../../../lib/api';
import type {
  CreateUserRequest,
  ListQuery,
  LoginAttempt,
  PagedResult,
  ResetUserPasswordRequest,
  ResetUserPinRequest,
  UpdateUserRequest,
  UserCredentialIssued,
  UserDetail,
  UserListItem,
} from './userTypes';

/** Serializes a ListQuery, dropping empty values so the URL stays readable. */
function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const usersApi = {
  /** Soft-deletes every record in the module at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/users/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<UserListItem>>(`/api/users${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<UserDetail>(`/api/users/${id}`, { signal }),

  create: (request: CreateUserRequest) =>
    api<UserCredentialIssued>('/api/users', { method: 'POST', body: request }),

  update: (id: string, request: UpdateUserRequest) =>
    api<UserDetail>(`/api/users/${id}`, { method: 'PUT', body: request }),

  /** Soft delete — deactivates the account and revokes its sessions. */
  deactivate: (id: string) => api<void>(`/api/users/${id}`, { method: 'DELETE' }),

  /** The mandatory reversal of the soft delete. */
  activate: (id: string) => api<void>(`/api/users/${id}/activate`, { method: 'POST' }),


  /** Switches the PIN requirement. Its own key — see UsersController. */
  setPinRequirement: (id: string, required: boolean) =>
    api<void>(`/api/users/${id}/pin-requirement`, { method: 'POST', body: { required } }),

  /** Sets another account's PIN and signs it out everywhere. Returns nothing — the form holds the value. */
  resetPin: (id: string, request: ResetUserPinRequest) =>
    api<void>(`/api/users/${id}/reset-pin`, { method: 'POST', body: request }),

  resetPassword: (id: string, request: ResetUserPasswordRequest) =>
    api<UserCredentialIssued>(`/api/users/${id}/reset-password`, { method: 'POST', body: request }),

  loginActivity: (id: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<LoginAttempt>>(`/api/users/${id}/login-activity${toQueryString(query)}`, {
      signal,
    }),
};

// The roles catalog is owned by features/roles/rolesApi.ts. Import it from there —
// a second client for the same resource is drift (reference §2).
